/**
 * Manufacturing & Co-Packing — 17 documents.
 */
export const MANUFACTURING_DOCS = [
  {
    id: "co-packer-agreement",
    name: "Co-Packer Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["Contract Packing Agreement", "Copacker Agreement", "Tolling Agreement"],
    stages: ["pre-launch", "manufacturing", "launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Co-Packer",
    whenYouNeedIt:
      "Before your first commercial production run with a third-party manufacturer. Also before a pilot run if you are handing over your formula to get it.",
    whatItDoes:
      "Sets the terms under which a manufacturer produces your product: what they make, to what specification, at what price, who supplies the inputs, who owns the formula, and what happens when something goes wrong.",
    whyItMatters:
      "Your co-packer controls the physical existence of the product your entire business sells. A weak agreement shows up in three predictable ways: you cannot move production without losing your formula, you are holding six figures of obsolete ingredients you never agreed to buy, or a quality failure lands on you because the recall clause was silent. Founders negotiate the per-case price hard and skim the rest — the rest is where the money is.",
    majorTerms: [
      "Minimum order quantity (MOQ) per run and per year",
      "Per-case or per-unit tolling price",
      "Price increase mechanism and notice period",
      "Production scheduling and lead times",
      "Rolling forecast obligations",
      "Purchase order and payment terms",
      "Deposits and prepayment",
      "Who buys and owns raw materials and packaging",
      "Yield loss and production variance allowance",
      "Product specifications and acceptance standards",
      "Term, renewal and termination rights",
      "Exclusivity, if any",
      "Insurance limits",
      "Tooling and change-part ownership",
    ],
    clausesToWatch: [
      {
        clause: "Formula and intellectual property ownership",
        why: "If the agreement is silent, or the co-packer claims ownership of 'improvements', you may not be able to take your own product to another manufacturer.",
      },
      {
        clause: "Excess and obsolete materials",
        why: "Ingredients and packaging bought against a forecast become your liability when demand shifts, often without any cap.",
      },
      {
        clause: "Yield loss and variance",
        why: "An unusually generous variance allowance means you pay for product you never receive on every single run.",
      },
      {
        clause: "Recall responsibility and cost allocation",
        why: "Recalls are expensive and fast-moving. If the clause does not say who pays for product, freight, retailer fees and notification, you will be arguing about it mid-crisis.",
      },
      {
        clause: "Termination and transition assistance",
        why: "Without an obligation to hand back specifications, remaining inventory and materials on exit, leaving is far harder than arriving.",
      },
      {
        clause: "Price increases",
        why: "An unlimited right to raise price on short notice removes any ability to plan your margin or hold retail pricing.",
      },
      {
        clause: "Indemnification and limitation of liability",
        why: "A cap set at the value of the affected run can leave you carrying the true cost of a defective product event.",
      },
      {
        clause: "Exclusivity or non-compete on the category",
        why: "A co-packer that cannot make competing products may cost more; one that can may share a line with a direct competitor.",
      },
    ],
    cpgIssues: [
      "Allergen control and changeover procedures matter enormously if the facility also runs nuts, dairy or gluten — the agreement should reference the allergen program, not assume it.",
      "Certificates of Analysis: specify which tests run on every lot, who pays, and how long records are retained, because retailers and insurers will ask for them.",
      "Shelf-life and stability results are the co-packer's process as much as your formula; tie acceptance to the specification, not to a verbal standard.",
      "Facility certifications (SQF, BRC, organic, kosher, non-GMO) must be maintained during the term — a lapsed certification can pull you off a retailer's shelf.",
      "Lot coding and traceability need to be specific enough to support a recall down to a single production run.",
      "For beverages, fill volume tolerance and net-content compliance are a specification issue with regulatory consequences, not just a quality preference.",
    ],
    founderMistakes: [
      "Signing the co-packer's standard form because the relationship felt collaborative, then discovering the formula ownership clause when trying to add a second manufacturer.",
      "Agreeing to a rolling forecast without understanding that the forecast becomes a binding purchase commitment for materials.",
      "Never obtaining a signed specification sheet, leaving 'correct product' undefined when a run comes out wrong.",
      "Assuming the co-packer's product liability insurance covers the brand, without being named as an additional insured.",
      "Accepting a long initial term with no termination for convenience because the MOQ pricing looked better.",
    ],
    negotiationPoints: [
      "Ask for explicit written confirmation that you own the formula, all specifications and any improvements to them.",
      "Cap your exposure on excess and obsolete materials, and set a window in which they must be used or invoiced.",
      "Negotiate a defined price-increase mechanism: pass-through of documented ingredient cost only, with notice and a right to terminate.",
      "Ask to be named as an additional insured and to receive certificates of insurance annually.",
      "Request transition assistance on termination: return of materials, specifications and remaining finished goods within a stated period.",
      "Push for a mutual termination-for-convenience right with reasonable notice rather than a fixed multi-year lock.",
      "Set a variance allowance you have actually modeled against your unit economics.",
    ],
    redFlags: [
      "Any clause granting the co-packer ownership of, or a license to, your formula or improvements.",
      "Automatic renewal for a further multi-year term unless you give notice far in advance.",
      "Unlimited or undefined right to increase pricing.",
      "No stated obligation to return your materials or specifications on termination.",
      "Silence on recall cost allocation.",
      "Liability capped at an amount far below the cost of a single production run.",
      "An exclusivity obligation running against you with no corresponding volume or service commitment from them.",
    ],
    beforeYouSign: [
      "A signed, dated product specification sheet you both agree describes the product.",
      "A quality agreement, or a quality section detailed enough to serve as one.",
      "The co-packer's current facility certifications and audit results.",
      "Certificates of insurance naming the coverage types and limits.",
      "Your own landed-cost model, including the variance allowance and any material minimums.",
    ],
    related: [
      "manufacturing-nda",
      "quality-agreement",
      "product-specification-sheet",
      "recall-responsibility-agreement",
      "formula-ownership-agreement",
      "ingredient-supply-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This agreement governs your product's existence and your exposure in a recall — the two things hardest to fix after the fact.",
    templateId: "co-packer-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "contract-manufacturing-agreement",
    name: "Contract Manufacturing Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["CMO Agreement", "Contract Manufacturer Agreement", "Finished Goods Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "growth"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Contract Manufacturer",
    whenYouNeedIt:
      "When the manufacturer will buy the ingredients and packaging themselves and invoice you for finished cases, rather than converting materials you supply.",
    whatItDoes:
      "Creates a finished-goods purchase relationship: the manufacturer sources inputs, produces to your specification and sells you completed cases at a per-case price, with the agreement governing capacity, cost basis, quality and what happens when a lot fails.",
    whyItMatters:
      "The difference from a co-packing or tolling deal is who carries the material risk and who can see the input cost. When the manufacturer buys the inputs, your per-case price hides an ingredient cost you cannot verify, and a claimed commodity move arrives as a price increase you have no way to check. It also means the component inventory sits on their books — which feels like a relief until you try to leave and the exit is priced as a settlement of everything they bought against your forecast.",
    majorTerms: [
      "Per-case finished-goods price and what it includes",
      "Cost basis and pass-through mechanism for ingredients and packaging",
      "Price increase notice period and supporting documentation",
      "Minimum order quantity per run and annual volume commitment",
      "Capacity reservation and allocation during tight supply",
      "Rolling forecast and the binding portion of it",
      "Purchase order acceptance and production lead times",
      "Payment terms, deposits and prepayment on long-lead inputs",
      "Ownership of raw materials, packaging and work in progress",
      "Excess, slow-moving and obsolete material liability",
      "Product specification and acceptance criteria",
      "Rejected product, rework and credit process",
      "Audit and inspection rights",
      "Term, renewal, termination and transition assistance",
    ],
    clausesToWatch: [
      {
        clause: "Pricing basis and cost transparency",
        why: "A single bundled per-case price with no visibility into input cost means you cannot tell a genuine commodity move from a margin grab, and you cannot model your own cost of goods.",
      },
      {
        clause: "Forecast conversion into a binding commitment",
        why: "A forecast you gave as planning guidance can become an enforceable order for materials, and the first firm number you learn is the invoice for what they bought.",
      },
      {
        clause: "Capacity allocation",
        why: "Without a reserved slot or a stated allocation rule, a larger customer's rush order pushes your run and you miss a retailer's delivery window.",
      },
      {
        clause: "Ingredient sourcing and substitution rights",
        why: "If they can change suppliers without telling you, the allergen profile, certification status or origin of your product can change while the label stays the same.",
      },
      {
        clause: "Rejected product, rework and credit",
        why: "If the remedy for a bad lot is a credit against future production, you finance their mistake and only recover it if you keep buying.",
      },
      {
        clause: "Formula and specification ownership",
        why: "A manufacturer who developed part of the formulation may claim rights in it, which turns a routine change of manufacturer into a negotiation you cannot win.",
      },
      {
        clause: "Termination and transition assistance",
        why: "Without an obligation to release specifications, remaining components and finished goods on exit, the practical cost of leaving is a full re-launch.",
      },
      {
        clause: "Audit rights",
        why: "No right to audit cost, quality records or the facility means you are relying entirely on their reporting when a retailer or an insurer asks you to prove something.",
      },
    ],
    cpgIssues: [
      "When the manufacturer buys ingredients, an unannounced supplier swap can change allergen status, organic or non-GMO eligibility, or declared country of origin — the agreement should require notice before the swap, not after the run.",
      "Certificates of Analysis should be named per lot with the specific parameters and test methods, because a COA that only reports what the supplier felt like reporting proves nothing to a retailer.",
      "Shelf-life data was generated on a specific process at a specific plant; a facility or line change can quietly invalidate the dating printed on your pack.",
      "The plant's FDA facility registration and its certifications — organic handler, kosher, non-GMO, third-party food safety schemes — belong to them and not to you, so the agreement should say who keeps each current and how fast you are told if one lapses.",
      "Lot coding and traceability must let you reconstruct which ingredient lots went into which pallet, or a narrow issue becomes a wide recall.",
      "Net contents and fill tolerance belong in the specification with a stated acceptance range, not left to the plant's habit.",
    ],
    founderMistakes: [
      "Comparing a contract manufacturer's per-case price to a co-packer's tolling fee without adding the ingredients and packaging to the tolling side, then choosing the more expensive deal because the number looked smaller.",
      "Giving a twelve-month forecast to win better pricing, and discovering months later that the last nine months of it were treated as a material purchase commitment.",
      "Accepting 'manufacturer's standard ingredients' in the specification, then finding a supplier had been changed when a customer complaint surfaced a taste difference.",
      "Never asking for the cost build-up, so the first price increase arrived with no documentation and no basis to challenge it.",
      "Assuming the finished goods were theirs until delivery and not insuring inventory sitting at the plant.",
    ],
    negotiationPoints: [
      "Ask for an open-book or indexed cost basis on the largest input lines, with price changes tied to documented movement rather than assertion.",
      "Limit the binding portion of the forecast to a stated near-term window and treat the rest as non-binding planning.",
      "Ask for a stated production slot or allocation rule so your run is not displaced by a larger account.",
      "Require written notice and your approval before any change of ingredient supplier, formulation, process or facility.",
      "Negotiate cash credit or replacement product on rejected lots, not a credit that only exists if you keep ordering.",
      "Secure transition assistance on exit: release of specifications, transfer of remaining components at cost and a final production run on notice.",
      "Ask to be named as an additional insured on their product liability policy and to receive annual certificates.",
    ],
    redFlags: [
      "A price that can be changed at will with no notice period and no documentation requirement.",
      "A forecast clause that converts planning numbers into a purchase obligation without a stated cap.",
      "Any claim by the manufacturer to ownership of, or a license to, your formula, specification or improvements.",
      "Rejected product remedied only by credit against future orders.",
      "No obligation to notify you before changing ingredient suppliers, process or production site.",
      "No audit or facility inspection right of any kind.",
      "Automatic multi-year renewal with a long notice window to escape it.",
    ],
    beforeYouSign: [
      "A signed product specification covering ingredients, process, finished attributes and acceptance tolerances.",
      "The cost build-up behind the quoted per-case price, even at a summary level.",
      "Current facility certifications, the most recent third-party audit result and the food safety plan summary.",
      "Certificates of insurance showing coverage types and limits, and confirmation you can be added as an additional insured.",
      "Your own landed-cost model including freight, storage and the material commitments implied by the forecast.",
    ],
    related: [
      "co-packer-agreement",
      "toll-manufacturing-agreement",
      "quality-agreement",
      "product-specification-sheet",
      "manufacturing-change-control-agreement",
      "ingredient-supply-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "manufacturing-services-agreement",
    name: "Manufacturing Services Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["MSA (Manufacturing)", "Production Services Agreement", "Blending and Filling Services Agreement"],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "When what you are actually buying is capacity and labour — line time, blending, filling, packing — rather than a finished case of product.",
    whatItDoes:
      "Frames the relationship as services performed on your behalf, usually priced by batch, by line hour or by unit converted, and sets scheduling, quality, staffing and record-keeping obligations around that service.",
    whyItMatters:
      "The services frame changes what you can hold them to. A seller of goods owes you conforming product; a provider of services often owes only performance of the service with reasonable care. Written loosely, that difference means a run that comes out wrong is billed as line time you used rather than product you never received. It also means the quality obligations have to be written in explicitly, because there is no implied product to be defective.",
    majorTerms: [
      "Pricing unit: per batch, per line hour, per case converted or a blend",
      "Minimum committed line time or batch volume",
      "Scheduling process, production windows and changeover time",
      "Lead time to book a run and the cost of cancelling one",
      "Materials supplied by each party and who pays for shortfalls",
      "Yield loss and production variance allowance",
      "Standard of performance and what a failed run means for the invoice",
      "Rework, reprocessing and second-run pricing",
      "Production records, batch documentation and retention period",
      "Staffing, line assignment and supervision",
      "Insurance and liability allocation",
      "Term, termination and notice",
    ],
    clausesToWatch: [
      {
        clause: "Standard of performance",
        why: "If they owe only 'commercially reasonable efforts', a run that fails specification may still be fully billable, and you have no product and a bill.",
      },
      {
        clause: "Yield loss and variance allowance",
        why: "On a services deal you supplied the materials, so every point of allowed variance is your ingredient and your packaging leaving the building as waste.",
      },
      {
        clause: "Changeover and set-up time billing",
        why: "If sanitation, allergen changeover and line set-up are billed as your line hours, a short run can cost more in preparation than in production.",
      },
      {
        clause: "Cancellation and rescheduling fees",
        why: "A booked slot you cannot fill because an ingredient is late can be billed in full while you also carry the delay.",
      },
      {
        clause: "Rework responsibility",
        why: "If rework caused by their process error is billed as additional services, their mistakes become your line-time purchases.",
      },
      {
        clause: "Batch records and retention",
        why: "Without a right to the batch records, you cannot reconstruct what happened on a run when a customer complaint or a retailer question arrives months later.",
      },
    ],
    cpgIssues: [
      "Allergen changeover time is real production time — the agreement should say who pays for it and whether your product is scheduled after an allergen-containing run.",
      "Sanitation and flush volumes at the start of a run consume your ingredients if you supplied them; the variance allowance should account for it explicitly.",
      "Batch records, retained samples and lot coding are the evidence base for any later quality dispute or trace exercise, so retention period and access rights matter more than they sound.",
      "Line speed and equipment capability determine whether your can, bottle or closure actually runs — a service that cannot run your component is a scheduling problem you pay for.",
      "Certificates of Analysis and finished-product testing need to be assigned to one party by name, because a services frame makes it easy for both sides to assume the other is testing.",
    ],
    founderMistakes: [
      "Buying line hours without a written specification, so 'the run failed' became a debate with no reference point and the invoice stood.",
      "Not modelling changeover and sanitation into the cost per case, then finding the effective conversion cost on short runs was far higher than quoted.",
      "Supplying materials against a generous variance allowance nobody had checked against the unit economics, and writing off ingredient every single run.",
      "Never asking for batch records, then being unable to answer a retailer's questions about a specific lot.",
      "Booking a production slot before packaging arrived and paying a cancellation fee on a run that never happened.",
    ],
    negotiationPoints: [
      "Tie payment to production that meets the written specification, not simply to line time consumed.",
      "Set the variance allowance from your own yield model and ask for a reconciliation after the first few runs.",
      "Ask that rework caused by their process error is performed at their cost.",
      "Separate changeover and sanitation time from productive line time in the pricing so you can see what you are buying.",
      "Request copies of batch records and retained samples for every run as a standing obligation, not on request.",
      "Negotiate a rescheduling window that reflects real material lead times rather than a fixed short notice period.",
    ],
    redFlags: [
      "No stated quality standard tied to a specification document.",
      "Full billing for runs that fail specification regardless of cause.",
      "An open-ended variance allowance, or one expressed without a number.",
      "No right to batch records, retained samples or lot data.",
      "Cancellation fees with no corresponding obligation on them for a missed production slot.",
    ],
    beforeYouSign: [
      "A signed specification the services will be measured against.",
      "A written yield model covering flush, changeover and expected loss.",
      "Confirmation the line can physically run your packaging components at the quoted speed.",
      "Certificates of insurance, including coverage for your materials while in their possession.",
    ],
    related: [
      "co-packer-agreement",
      "toll-manufacturing-agreement",
      "production-agreement",
      "quality-agreement",
      "product-specification-sheet",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "toll-manufacturing-agreement",
    name: "Toll Manufacturing Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["Tolling Agreement", "Toll Processing Agreement", "Conversion Agreement"],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Toll Manufacturer",
    whenYouNeedIt:
      "When you buy and own every input — ingredients, cans, labels, cartons — and pay the plant only to convert them into finished product.",
    whatItDoes:
      "Sets a conversion-fee relationship in which title to materials, work in progress and finished goods stays with you throughout, and the manufacturer is paid a toll for processing rather than for selling you a product.",
    whyItMatters:
      "Tolling gives you the best cost visibility of any manufacturing structure and the worst inventory exposure. Everything sitting in that building is yours: the pallets of cans, the tote of concentrate, the finished goods waiting for a truck. If the plant floods, if inventory is miscounted, if you fall behind on invoices and they hold your goods, the loss lands on you. The word 'tolling' also gets used loosely for ordinary co-packing, so read the materials-ownership section rather than the title of the document.",
    majorTerms: [
      "Conversion fee per batch, per case or per unit",
      "Minimum volume or minimum monthly conversion commitment",
      "Which materials you supply and the delivery window for each",
      "Title and risk of loss over materials, work in progress and finished goods",
      "Storage terms, storage fees and free-storage period",
      "Insurance on your inventory while it is in their possession",
      "Yield loss, shrinkage and reconciliation of materials consumed",
      "Scheduling, lead times and slot booking",
      "Inventory reporting, cycle counts and physical count rights",
      "Right to remove your materials on demand and on termination",
      "Security interests, liens and set-off rights over your goods",
      "Term, termination and notice",
    ],
    clausesToWatch: [
      {
        clause: "Risk of loss while materials are in their possession",
        why: "If risk stays with you and their insurance does not cover goods held for others, a warehouse fire destroys inventory nobody is insuring.",
      },
      {
        clause: "Lien, possessory and set-off rights",
        why: "A broad right to hold your materials against amounts owed means a billing dispute can freeze your entire inventory and your ability to ship.",
      },
      {
        clause: "Material reconciliation and shrinkage",
        why: "Without a reconciliation obligation, the difference between materials delivered and product produced is simply absorbed, and it is always absorbed by you.",
      },
      {
        clause: "Storage fees and free-storage window",
        why: "Cans and cartons take enormous floor space; storage charged by pallet position from day one can quietly exceed the conversion fee itself.",
      },
      {
        clause: "Right to retrieve materials",
        why: "If you cannot collect your own ingredients and packaging on short notice, moving production means abandoning what you already paid for.",
      },
      {
        clause: "Yield loss allowance",
        why: "In a tolling deal the allowance is measured in your ingredients and your packaging, so a loose number is a recurring write-off on every run.",
      },
    ],
    cpgIssues: [
      "Your inventory sitting at a co-packer is often the single largest asset the business owns, and a standard business policy may not cover property held at another company's site — confirm it, in writing, before the first delivery.",
      "Cans, bottles and cartons consume pallet positions out of proportion to their value, so free-storage windows and pallet-position pricing decide whether a big packaging buy actually saved money.",
      "FIFO or FEFO rotation of your ingredient inventory needs to be an obligation, because expired ingredient in their racking is your write-off.",
      "Material reconciliation should reference lot numbers so that a trace exercise can follow a specific ingredient lot into specific finished pallets.",
      "Ingredients with limited shelf life bought to hit a supplier minimum can expire in the plant's warehouse before you have volume to consume them.",
    ],
    founderMistakes: [
      "Shipping a full truckload of cans to the co-packer without confirming who insured them, and having no cover when they were damaged in a warehouse incident.",
      "Assuming free storage was indefinite, then receiving months of backdated pallet-position charges.",
      "Never running a material reconciliation, so a persistent shrinkage pattern went unnoticed across a year of production.",
      "Signing a tolling agreement with a broad set-off right, then being unable to move inventory during a disputed invoice.",
      "Buying ingredients to a supplier minimum with a shorter shelf life than the time it took to produce through them.",
    ],
    negotiationPoints: [
      "Require them to carry insurance covering property of others in their care, and to provide a certificate confirming it.",
      "Set a free-storage window tied to your production cycle, with pallet-position pricing stated after it.",
      "Ask for monthly material reconciliation by lot, with a variance threshold that triggers investigation.",
      "Narrow any lien or set-off right to genuinely undisputed amounts, and exclude it from applying to raw materials.",
      "Include an express right to remove your materials on stated notice, including on termination.",
      "Ask for rotation obligations and shelf-life monitoring on the ingredients they hold for you.",
    ],
    redFlags: [
      "Silence on who insures your materials while they are on site.",
      "A broad lien over all of your property for any amount claimed to be owed.",
      "No reconciliation of materials consumed against product produced.",
      "Storage fees that begin immediately with no stated rate.",
      "No stated right to remove your own inventory.",
      "A yield allowance expressed only as 'industry standard'.",
    ],
    beforeYouSign: [
      "Written confirmation of who insures your materials on their premises, and a certificate evidencing it.",
      "The storage fee schedule and the free-storage period in writing.",
      "A yield and shrinkage model built from your own bill of materials.",
      "A signed specification and a quality section detailed enough to define a good run.",
    ],
    related: [
      "co-packer-agreement",
      "contract-manufacturing-agreement",
      "manufacturing-services-agreement",
      "warehouse-agreement",
      "packaging-supply-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "private-label-manufacturing-agreement",
    name: "Private Label Manufacturing Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["White Label Agreement", "Stock Formula Agreement", "Private Label Supply Agreement"],
    stages: ["pre-launch", "manufacturing", "launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Private Label Manufacturer",
    whenYouNeedIt:
      "When you are launching on a manufacturer's existing formula with your brand on the pack, rather than developing your own product.",
    whatItDoes:
      "Licenses you the right to sell the manufacturer's product under your label, and sets volume, pricing, exclusivity and how much of the formulation you can influence.",
    whyItMatters:
      "Private label is the fastest and cheapest way to a launch, and the structure with the least under you. You own the brand, the artwork and the customer relationship; they own the liquid or the powder. That means your cost of goods, your ability to reformulate, and in practice your ability to keep selling at all sit with a company you cannot replace without starting product development from zero. Founders take this deal to get to shelf, then discover the exit price two years later when it is time to raise money on a product they do not control.",
    majorTerms: [
      "Which formula is supplied and whether it is a stock or modified version",
      "Formula ownership and whether any modifications become yours",
      "Exclusivity: whether the same formulation is sold to competitors",
      "Volume minimums required to hold any exclusivity",
      "Per-unit pricing, tiers and price increase mechanism",
      "Minimum order quantity and lead times",
      "Label and artwork responsibility and approval",
      "Who is named as manufacturer or distributor on the pack",
      "Product changes and notification obligations",
      "Regulatory documentation and testing responsibility",
      "Term, renewal and termination",
      "Post-termination sell-off period for remaining stock",
    ],
    clausesToWatch: [
      {
        clause: "Formula ownership and modification rights",
        why: "If modifications you paid to develop remain theirs, you have funded improvements to a product your competitor can buy next quarter.",
      },
      {
        clause: "Exclusivity scope and the volume required to hold it",
        why: "Exclusivity that lapses on a volume miss can convert your differentiated product into a commodity available to anyone the month after a soft quarter.",
      },
      {
        clause: "Price increase mechanism",
        why: "A supplier who knows you have no alternative source has unusual pricing power, and an unrestricted increase right converts that into margin.",
      },
      {
        clause: "Product change and reformulation rights",
        why: "If they can reformulate without your approval, your label claims, allergen statement and customer expectations can all become wrong overnight.",
      },
      {
        clause: "Termination and sell-off rights",
        why: "Without a sell-off period and a final purchase right, termination leaves you with printed packaging for a product you can no longer buy.",
      },
      {
        clause: "Responsibility for label content and claims",
        why: "The brand on the pack is the one customers and retailers come to, so an agreement that leaves label accuracy entirely with you while the formula stays with them puts the risk on the party with the least information.",
      },
    ],
    cpgIssues: [
      "The ingredient statement and allergen declaration on your label describe their formula — you need a contractual right to accurate, current ingredient and allergen data every time anything changes.",
      "Facility registration, food safety programs and certifications belong to their plant; if a certification lapses, your product loses the claim on the front of your own pack.",
      "Shelf life and storage conditions are set by their process and testing, so your date coding depends on data you did not generate and should be entitled to see.",
      "If they also sell the same base formula to others, your 'unique' product may sit next to a near-identical competitor on the same shelf under a different label.",
      "Nutrition and claim substantiation data comes from them; get it in writing before it goes on a pack a retailer will hold you to.",
    ],
    founderMistakes: [
      "Paying for flavour and format modifications without securing ownership of them, then watching a competitor launch the same profile.",
      "Accepting exclusivity tied to volume minimums set at optimistic launch projections, and losing exclusivity in the first slow quarter.",
      "Building an entire brand on a stock formula, then finding the business was unfundable because there was no product IP to buy.",
      "Not requiring notice of reformulation, and only learning the ingredient statement had changed when a customer flagged it.",
      "Printing a year of packaging against a supply relationship that could be terminated on short notice.",
    ],
    negotiationPoints: [
      "Ask that any modification developed at your cost and to your brief becomes your property, or at minimum is exclusive to you.",
      "Negotiate category or channel exclusivity with realistic volume thresholds and a cure period before it lapses.",
      "Require written notice and approval before any change to the formula, ingredient supplier or process.",
      "Ask for a defined price mechanism with notice, rather than a right to increase at will.",
      "Secure a post-termination sell-off period and a right to a final production run sized to your packaging inventory.",
      "Get a standing obligation to provide current ingredient, allergen, nutrition and shelf-life documentation.",
    ],
    redFlags: [
      "No notice obligation before reformulation.",
      "Exclusivity available only at volumes far beyond your realistic plan.",
      "A right to terminate on short notice with no sell-off period.",
      "Refusal to provide ingredient, allergen or shelf-life documentation in writing.",
      "Ownership of modifications you funded staying with the manufacturer.",
      "An unrestricted right to raise prices.",
    ],
    beforeYouSign: [
      "Current written ingredient, allergen, nutrition and shelf-life documentation for the exact formula being supplied.",
      "Clarity in writing on whether the same formulation is sold to others in your category.",
      "The plant's certifications relevant to any claim you intend to put on the label.",
      "A costed plan for what happens if this supply ends: what it takes to reformulate elsewhere and how long it takes.",
    ],
    related: [
      "co-packer-agreement",
      "contract-manufacturing-agreement",
      "formula-ownership-agreement",
      "packaging-artwork-ownership",
      "product-specification-sheet",
      "brand-license-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "pilot-run-agreement",
    name: "Pilot Run Agreement",
    category: "manufacturing",
    subcategory: "Trial and scale-up",
    aliases: ["Trial Run Agreement", "First Production Run Agreement", "Test Batch Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Before the first trial batch on a plant's commercial equipment, when you are proving the formula scales and the plant can actually run it.",
    whatItDoes:
      "Covers a single limited production trial: what will be made, who supplies materials, what it costs, what data comes out of it, and what can be done with the product afterwards.",
    whyItMatters:
      "The pilot run is usually the first time your complete formula, process and packaging leave your hands. Two things get lost here. The first is the formula itself, handed over on a purchase order with no confidentiality behind it. The second is the process knowledge the trial generates — line settings, hold times, fill temperatures — which is the difference between a formula and a product you can make somewhere else. Founders treat the pilot as a small purchase and give away the two most valuable things they own.",
    majorTerms: [
      "Scope of the trial: batch size, number of runs, SKUs",
      "Cost of the run and what happens if it fails",
      "Who supplies ingredients and packaging for the trial",
      "Ownership of the resulting process data and settings",
      "Confidentiality covering the formula, process and results",
      "Whether the pilot product may be sold or must be disposed of",
      "Testing performed on the trial product and who pays",
      "Whether pilot pricing carries into commercial production",
      "Scheduling, line time and cancellation terms",
      "Path to a commercial agreement if the trial succeeds",
    ],
    clausesToWatch: [
      {
        clause: "Ownership of process data and scale-up know-how",
        why: "If the settings and process developed on your product belong to the plant, you can take your formula elsewhere but not the knowledge of how to make it.",
      },
      {
        clause: "Confidentiality covering the trial",
        why: "A pilot run frequently happens on a purchase order alone, which means your formula went into a plant with nothing preventing its use or disclosure.",
      },
      {
        clause: "Sale of pilot product",
        why: "Trial product often lacks final labelling, shelf-life data or a complete testing record, and selling it puts unverified product in a customer's hands.",
      },
      {
        clause: "Whether pilot pricing binds commercial pricing",
        why: "An attractive trial price with no commitment on production pricing gives them full leverage once the formula is proven on their line.",
      },
      {
        clause: "Failure of the run and who pays",
        why: "If every outcome is billable, a failed trial caused by an equipment limitation they knew about is still your cost.",
      },
    ],
    cpgIssues: [
      "Pilot product usually has no stability or shelf-life data behind it, so any date code on it is an assumption rather than a result.",
      "Trial runs often use hand-applied or draft labels, which means the pack in front of you is not the pack a retailer would receive.",
      "Allergen changeover and sanitation before a small trial can cost more than the trial itself; agree who bears it.",
      "Scale-up frequently changes the product — viscosity, colour, mouthfeel and carbonation behave differently at line speed than in a lab — so plan for a second trial rather than assuming one is enough.",
      "Samples retained from the trial are the only physical evidence of what was made; ask for retained samples and the batch record.",
    ],
    founderMistakes: [
      "Sending the full formula for a trial run on a purchase order with no confidentiality agreement in place.",
      "Selling pilot product to early accounts, then having no shelf-life data when a case came back cloudy.",
      "Assuming the trial price reflected production economics, and discovering commercial pricing was materially different once the plant had the process.",
      "Not asking for the batch record or line settings, and having to re-develop the process at the next manufacturer.",
      "Treating one successful pilot as proof, then hitting a completely different result on the first full-length commercial run.",
    ],
    negotiationPoints: [
      "Put a confidentiality agreement in place before any formula or specification is sent, not alongside the purchase order.",
      "Ask that process settings and trial data developed on your product belong to you, with the plant free to use its own general know-how.",
      "Agree in writing whether the trial output can be sold, and under what labelling and testing conditions.",
      "Ask for indicative commercial pricing and MOQ before the trial, so the trial is not a negotiation you have already lost.",
      "Request the batch record, retained samples and any test results as deliverables of the run.",
      "Agree who pays if the run fails for reasons within the plant's control.",
    ],
    redFlags: [
      "No confidentiality agreement covering the formula before it is sent.",
      "A claim to ownership of results, process data or improvements arising from the trial.",
      "No written statement of what the trial costs or what happens if it fails.",
      "Pressure to sell trial product to recover the cost of the run.",
      "Refusal to indicate commercial pricing or MOQ until after the trial.",
    ],
    beforeYouSign: [
      "A signed confidentiality agreement covering formula, specification and process.",
      "A written trial protocol: batch size, parameters to be measured and what counts as success.",
      "Agreement on who supplies and who pays for the trial materials.",
      "Indicative commercial terms so the trial has a defined path forward.",
    ],
    related: [
      "co-packer-agreement",
      "manufacturing-nda",
      "pilot-production-agreement",
      "product-specification-sheet",
      "stability-testing-agreement",
      "formula-ownership-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "production-agreement",
    name: "Production Agreement",
    category: "manufacturing",
    subcategory: "Core manufacturing",
    aliases: ["Production Run Agreement", "Manufacturing Terms", "Run Agreement"],
    stages: ["manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "For scheduled production runs with a manufacturer you already work with, or for a defined project such as a seasonal SKU or a limited edition, where a full manufacturing agreement is more paper than the job needs.",
    whatItDoes:
      "Covers the mechanics of getting product made — scheduling, lead times, purchase orders, materials, acceptance and payment — usually for a defined set of runs rather than an open-ended relationship.",
    whyItMatters:
      "This is the thinnest document in the manufacturing family and the one founders most often end up with, because it is what a plant hands over when you ask to get something made. It handles the run and stays silent on everything that becomes expensive later: who owns the formula, who pays in a recall, what happens to your materials when the relationship ends. A production agreement is fine for what it covers, and dangerous if it is the only thing you have.",
    majorTerms: [
      "Products, SKUs and volumes covered",
      "Production scheduling and committed run dates",
      "Lead time from purchase order to production",
      "Purchase order process and acceptance",
      "Materials supplied by each party and delivery deadlines",
      "Per-unit or per-run price and validity period",
      "Payment terms and deposit on scheduling",
      "Acceptance criteria and inspection window",
      "Rejected product and rework",
      "Cancellation and rescheduling",
    ],
    clausesToWatch: [
      {
        clause: "Scope of what the document actually covers",
        why: "A run-level agreement that says nothing about formula ownership, recall or transition leaves those questions to be settled during whatever crisis raises them.",
      },
      {
        clause: "Acceptance window and inspection",
        why: "A very short window to inspect and reject means defects found at the retailer, weeks later, are already your problem.",
      },
      {
        clause: "Price validity period",
        why: "Pricing quoted per run with no stated validity can move between the purchase order and the production date.",
      },
      {
        clause: "Materials deadlines and consequences",
        why: "If your packaging arrives late and the slot is billable, a supplier's delay becomes a charge with no corresponding obligation on the plant when they slip.",
      },
      {
        clause: "Cancellation and rescheduling rights",
        why: "One-sided cancellation terms let the plant move your run at will while you pay to move your own.",
      },
    ],
    cpgIssues: [
      "Run-level paperwork usually omits the recall cost allocation, which is the single most expensive silence in CPG manufacturing.",
      "Seasonal and limited-edition runs still generate obsolete packaging, so the agreement should say what happens to leftover components.",
      "Date coding, lot coding and the label version used on the run should be recorded on the document itself, because limited runs are the ones nobody can reconstruct later.",
      "A short production agreement rarely references the specification; attach the signed spec so 'correct product' has a definition.",
      "Certificates of Analysis and retained samples are easy to skip on a one-off run and are exactly what a retailer asks for when something goes wrong with it.",
    ],
    founderMistakes: [
      "Running an entire first year of production on purchase orders and a one-page production agreement, then having nothing to point to during a quality dispute.",
      "Not attaching the specification, so acceptance became a matter of opinion.",
      "Accepting a three-day inspection window on product shipping straight to a distributor, where nobody looked at it for a month.",
      "Paying a deposit to hold a slot with no obligation on the plant to hold the date.",
      "Leaving leftover printed cartons at the plant with no agreement on storage or disposal, and being invoiced for both.",
    ],
    negotiationPoints: [
      "Attach the signed product specification and reference it as the acceptance standard.",
      "Ask for an inspection window that reflects when the product is actually received and examined.",
      "Add a short recall cooperation and cost allocation section even in a run-level document.",
      "Make scheduling commitments mutual: if you pay to move a run, they should carry an obligation when they move it.",
      "State a price validity period covering the gap between order and production.",
    ],
    redFlags: [
      "No reference to any specification document.",
      "Silence on recall responsibility.",
      "Inspection or rejection windows measured in a few days from production rather than from receipt.",
      "Deposits that are non-refundable regardless of who causes a delay.",
      "Cancellation rights that run only in the plant's favour.",
    ],
    beforeYouSign: [
      "The signed product specification for the SKUs being produced.",
      "A written schedule with committed production dates and material deadlines.",
      "Confirmation of what happens to leftover materials after the run.",
    ],
    related: [
      "co-packer-agreement",
      "manufacturing-services-agreement",
      "product-specification-sheet",
      "recall-responsibility-agreement",
      "purchase-order-terms",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "manufacturing-nda",
    name: "Manufacturing NDA",
    category: "manufacturing",
    subcategory: "Confidentiality",
    aliases: ["Co-Packer NDA", "Plant NDA", "Formula Confidentiality Agreement"],
    stages: ["development", "pre-launch", "manufacturing"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer or Co-Packer",
    whenYouNeedIt:
      "Before you send a formula, specification, batch sheet or sample to any plant — including the ones that are only quoting and the ones a friend introduced you to.",
    whatItDoes:
      "Restricts what a manufacturer may do with your formula, process, specifications, supplier list and volumes: not just whether they can disclose it, but whether they can use it themselves or hand it to another customer.",
    whyItMatters:
      "Quoting a co-packing job requires the formula. That means the sourcing process itself puts your product in front of every plant you talk to, most of which also make products for your competitors. A confidentiality clause that only stops disclosure but says nothing about use lets a plant build a near-identical product for someone else without ever telling anyone your name. The formula is usually the only genuinely defensible thing a young CPG brand owns.",
    majorTerms: [
      "Definition of confidential information, including formula, process and suppliers",
      "No-use obligation as well as no-disclosure",
      "Prohibition on reverse engineering and analytical deconstruction of samples",
      "Who inside the plant may see it and on what terms",
      "Duration of the obligation, and indefinite protection for trade secrets",
      "Return or destruction of documents, samples and retained lots",
      "Exclusions for information already known or independently developed",
      "Treatment of the plant's own pre-existing formulations and platform know-how",
      "Residuals language, if any",
      "Remedies and the right to seek injunctive relief",
    ],
    clausesToWatch: [
      {
        clause: "No-use versus no-disclosure",
        why: "An agreement that only prohibits disclosure permits the plant to use your formula for its own products or another customer without ever naming you.",
      },
      {
        clause: "Residuals clause",
        why: "Language allowing use of anything retained in an employee's unaided memory can swallow the protection you thought you had over a formula.",
      },
      {
        clause: "Fixed expiry of confidentiality",
        why: "A formula protected for a short fixed term becomes freely usable on a date certain, which is not how a trade secret is meant to work.",
      },
      {
        clause: "Reverse engineering of samples",
        why: "Without an express prohibition, a plant can analyse the product you sent them and reconstruct the formulation from the result.",
      },
      {
        clause: "The manufacturer's pre-existing intellectual property carve-out",
        why: "A broad carve-out for their platform technology or base formulations can be drafted widely enough to cover the product you brought them.",
      },
      {
        clause: "Return and destruction of samples and retained lots",
        why: "Documents get returned and physical samples stay in a freezer, which is the version of your product that is easiest to analyse later.",
      },
    ],
    cpgIssues: [
      "Physical samples are the real disclosure — a competitor's analytical lab learns more from one bottle than from your specification sheet, so the agreement must cover samples and retained lots, not just documents.",
      "Plants routinely make competing products on the same lines, so the confidentiality obligation should extend to the fact of your volumes, launch timing and retailer commitments.",
      "Your supplier list and the specific ingredient grades you use are often as valuable as the formula and are visible to the plant on every delivery.",
      "Flavour house and ingredient supplier names on a batch sheet reveal your entire sourcing strategy; consider whether they need to appear at all in a quoting document.",
      "Co-packers may sub-contract part of a run; the obligation should follow the information to any sub-contractor.",
    ],
    founderMistakes: [
      "Sending the formula to five plants for quotes on the strength of an email introduction, with no agreement in place at any of them.",
      "Signing the plant's short mutual form without reading the residuals clause, and losing practical protection over the formula.",
      "Accepting a confidentiality term of a few years on a trade secret intended to last for the life of the brand.",
      "Never requesting return or destruction after a quote went nowhere, leaving the formula in a plant's file indefinitely.",
      "Assuming the confidentiality clause inside the co-packing agreement covered the earlier quoting process, which it did not.",
    ],
    negotiationPoints: [
      "Insist on an express no-use restriction covering the plant's own products as well as other customers.",
      "Strike or narrow any residuals clause.",
      "Ask for indefinite protection of trade secret material, with a defined term only for ordinary business information.",
      "Add an express prohibition on reverse engineering or analysing samples.",
      "Require return or certified destruction of documents, samples and retained lots on request.",
      "Extend the obligations to employees, contractors and any sub-contracted facility.",
    ],
    redFlags: [
      "Disclosure-only obligations with no restriction on use.",
      "A residuals clause permitting use of unaided memory.",
      "A broad carve-out for the manufacturer's own formulations that could be read to cover yours.",
      "Confidentiality expiring on a short fixed date with no trade secret carve-out.",
      "No obligation covering samples or physical product.",
      "One-way obligations that protect their information but not yours.",
    ],
    beforeYouSign: [
      "A written list of exactly what you intend to disclose, so you can check the definition covers it.",
      "Confirmation of whether the plant makes products in your category and for whom, to the extent they will say.",
      "A decision on what can be withheld at the quoting stage — many quotes can be produced without the complete formula.",
    ],
    related: [
      "co-packer-agreement",
      "formula-ownership-agreement",
      "recipe-formula-confidentiality-agreement",
      "trade-secret-protection-agreement",
      "supplier-nda",
      "mutual-nda",
    ],
    attorneyReview: "yes",
    templateId: "manufacturing-nda",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "quality-agreement",
    name: "Quality Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["Quality Terms", "Technical Agreement", "Quality Annex"],
    stages: ["pre-launch", "manufacturing", "launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Alongside your first co-packing or contract manufacturing agreement, and before a retailer or an insurer asks how quality is controlled at the plant making your product.",
    whatItDoes:
      "Divides quality responsibility between you and the plant across specifications, testing, sampling, release, rejection, deviations, complaints, records, audits, change notification and recall cooperation — a written answer to 'who does what' for every quality event.",
    whyItMatters:
      "Every quality dispute comes down to the same argument: whether the product met an agreed standard, and who was supposed to check. Without a quality agreement, that argument starts from nothing and is settled by whoever has more leverage, which is not you. It is also the document buyers, insurers and chain retailers ask for, and its absence signals that nobody has decided who is accountable for the product in the can.",
    majorTerms: [
      "The controlling specification and its version",
      "Testing plan: which tests, on what frequency, by whom",
      "Certificate of Analysis contents, methods and delivery",
      "Sampling plan and retained sample obligations",
      "Product release: who releases and on what evidence",
      "Rejection criteria and disposition of non-conforming product",
      "Deviation handling, investigation and corrective action",
      "Change notification and approval thresholds",
      "Complaint handling and information flow from both sides",
      "Records retention period and access rights",
      "Audit rights, frequency and notice",
      "Recall and withdrawal cooperation and traceability",
    ],
    clausesToWatch: [
      {
        clause: "Who has release authority",
        why: "If the plant releases product on its own judgement, product can ship before you have seen a single test result.",
      },
      {
        clause: "Testing responsibility and cost",
        why: "Where the plan is vague, both parties assume the other is testing and nobody is, which surfaces only when a result is needed.",
      },
      {
        clause: "Disposition of non-conforming product",
        why: "Silence here allows off-specification product to be reworked, downgraded or shipped as a second-quality run without your knowledge.",
      },
      {
        clause: "Change notification thresholds",
        why: "If only 'material' changes need notice and nobody defines material, ingredient supplier swaps and process changes happen silently.",
      },
      {
        clause: "Records retention and access",
        why: "Batch records, environmental results and COAs are the evidence in a trace exercise, and a short retention period destroys them before you need them.",
      },
      {
        clause: "Audit rights",
        why: "Without a right to audit or to receive third-party audit results, you are certifying to retailers a standard you have never verified.",
      },
      {
        clause: "Complaint information flow",
        why: "Complaints often arrive at the plant first; if they are not obliged to tell you, you learn about a pattern from a retailer instead.",
      },
    ],
    cpgIssues: [
      "Allergen control and validated changeover procedures are the highest-consequence quality item in most food and beverage plants and should be addressed by name, not folded into general sanitation language.",
      "Certificates of Analysis must name the parameters and the test methods; a COA that says 'conforms' proves nothing to a retailer or an insurer.",
      "Positive release — holding product until micro or other results are in — costs working capital and warehouse space, so decide deliberately whether you require it rather than discovering the plant ships on production data alone.",
      "Retained samples from every lot are the only physical evidence available months later when a complaint arrives; specify quantity, storage conditions and retention period.",
      "Lot coding must connect ingredient lots to finished pallets so a trace exercise can be narrow instead of total.",
      "Facility certification status — organic, kosher, non-GMO, third-party food safety schemes — should carry an obligation to notify you promptly if it changes.",
      "On smaller deals the quality agreement, the QA agreement, the food safety agreement and the specification agreement are usually folded into one document; that is fine, as long as the content survives the consolidation.",
    ],
    founderMistakes: [
      "Signing a co-packing agreement with a two-line quality clause, then having no basis to reject a run that tasted wrong but met no written standard.",
      "Assuming the co-packer tested every lot, and finding out during a retailer audit that finished-product testing had never been assigned to anyone.",
      "Accepting a records retention period shorter than the product's shelf life, so the batch record was gone before the complaint arrived.",
      "Not requiring notice of ingredient supplier changes, and only learning of a swap when a customer noticed a difference.",
      "Never requesting the plant's third-party audit results, then being unable to answer a chain retailer's vendor qualification questionnaire.",
    ],
    negotiationPoints: [
      "Name the controlling specification by version, and require a signed change before it moves.",
      "Assign every test to a party by name, with method, frequency and who pays.",
      "Require a COA with defined parameters and methods for each lot, delivered before or with shipment.",
      "Set records retention at least as long as shelf life plus a stated margin, with an access right.",
      "Ask for prompt notification of any deviation, hold, complaint or certification change affecting your product.",
      "Secure an annual audit right plus the right to receive third-party audit reports and corrective action plans.",
      "Require retained samples of every lot, stored under stated conditions.",
    ],
    redFlags: [
      "No named specification, or a specification referenced but never attached.",
      "Release authority resting entirely with the plant with no notice to you.",
      "No obligation to notify you of deviations, holds or complaints.",
      "Records retention shorter than the product's shelf life.",
      "No audit right and refusal to share audit results.",
      "Change notification limited to changes the plant alone considers significant.",
    ],
    beforeYouSign: [
      "The signed, versioned product specification the quality agreement will reference.",
      "The plant's current certifications and most recent third-party audit result.",
      "A written testing plan showing which tests are run, by whom and how often.",
      "Your own decision on positive release, and what it costs you in inventory and time.",
    ],
    related: [
      "co-packer-agreement",
      "quality-assurance-agreement",
      "food-safety-agreement",
      "product-specification-sheet",
      "recall-responsibility-agreement",
      "manufacturing-change-control-agreement",
    ],
    attorneyReview: "yes",
    templateId: "quality-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "quality-assurance-agreement",
    name: "Quality Assurance Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["QA Agreement", "QA Responsibility Matrix", "Quality Systems Agreement"],
    stages: ["manufacturing", "launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "When the relationship is large enough that decision rights inside the quality process need naming — who can hold product, who can release it, who signs off on a deviation — usually as volume grows or a chain retailer starts asking.",
    whatItDoes:
      "Documents the quality assurance function itself: batch record review, release authority, hold and disposition decisions, deviation and corrective action workflow, retained samples, trending and the named individuals or roles on each side.",
    whyItMatters:
      "A quality agreement says what the standards are. A QA agreement says who decides when they are not met, and how fast. That distinction matters at exactly the moment it is hardest to negotiate — when product is on hold, a retailer is waiting and every hour costs money. Where the two documents overlap heavily, and they do, the risk is signing both with different answers to the same question.",
    majorTerms: [
      "Named quality contacts and escalation path on both sides",
      "Batch record review and who performs it",
      "Release authority and the evidence required to release",
      "Hold authority: who can stop product moving, and how quickly",
      "Disposition decisions for held or non-conforming product",
      "Deviation reporting timeline and investigation ownership",
      "Corrective and preventive action process and follow-up",
      "Retained samples, storage and retention period",
      "Trending, periodic quality review and reporting cadence",
      "Order of precedence against the quality agreement and the supply agreement",
    ],
    clausesToWatch: [
      {
        clause: "Order of precedence between quality documents",
        why: "Signing a quality agreement and a QA agreement that answer the same question differently means the dispute is about which paper wins, not about the product.",
      },
      {
        clause: "Hold authority and response time",
        why: "If only the plant can place product on hold, you may be unable to stop a shipment you already know is wrong.",
      },
      {
        clause: "Deviation reporting timeline",
        why: "A deviation reported at the end of the month is a deviation you learned about after the product reached a retailer's shelf.",
      },
      {
        clause: "Disposition rights over non-conforming product",
        why: "Without your approval on disposition, off-specification product can be reworked or downgraded and sold in a form you never agreed to.",
      },
      {
        clause: "Corrective action follow-through",
        why: "An investigation with no obligation to close out or verify effectiveness produces a report and the same failure three months later.",
      },
    ],
    cpgIssues: [
      "Release decisions in food and beverage often depend on micro results that arrive after production, so the agreement should say whether product ships before those results and who carries that risk.",
      "Hold decisions are physically expensive when product is already palletised at a third-party warehouse; the workflow should reach the warehouse, not stop at the plant.",
      "Retained samples need defined storage conditions, because a retained sample held warm proves nothing about a product stored cold.",
      "Sensory deviation — the run that meets every number and still tastes wrong — needs a defined route, or it becomes an argument with no process behind it.",
      "Where several quality documents exist, the allergen program and change notification obligations must be identical in all of them.",
    ],
    founderMistakes: [
      "Signing a QA agreement on top of an existing quality agreement without checking that the two agreed, and creating a contradiction that surfaced during a hold.",
      "Not naming a quality contact, so a deviation notice went to a general inbox and sat there.",
      "Having no hold authority of their own, and watching product they believed was defective ship to a distributor.",
      "Accepting deviation reporting on a monthly summary basis instead of promptly per event.",
      "Never following up on corrective actions, so the same line failure recurred across three runs.",
    ],
    negotiationPoints: [
      "Name individuals or roles on both sides with contact details and a stated escalation time.",
      "Secure your own hold authority reaching finished goods wherever they are stored.",
      "Require prompt per-event deviation notice rather than periodic summaries.",
      "Ask for approval rights over disposition of non-conforming product.",
      "Set an order-of-precedence clause across the quality, QA and supply documents.",
      "Require corrective action close-out with effectiveness verification and a copy to you.",
    ],
    redFlags: [
      "No named contacts and no escalation timeline.",
      "Release and hold authority sitting entirely with the plant.",
      "Deviations reported only in periodic summaries.",
      "Disposition of non-conforming product decided without your approval.",
      "Conflicting terms with an existing quality agreement and no precedence clause.",
    ],
    beforeYouSign: [
      "Your existing quality agreement, read side by side with this one for contradictions.",
      "A decision on who inside your business holds quality authority and can be reached quickly.",
      "The plant's deviation and corrective action procedure, at least in summary.",
    ],
    related: [
      "quality-agreement",
      "food-safety-agreement",
      "specification-agreement",
      "manufacturing-change-control-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "food-safety-agreement",
    name: "Food Safety Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["Food Safety Responsibility Agreement", "HACCP Responsibility Agreement", "Food Safety Annex"],
    stages: ["pre-launch", "manufacturing", "launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "When you need a written allocation of food safety responsibility between you and the plant — commonly triggered by a retailer's vendor qualification, an insurer's questions, or your own first serious look at what the plant actually runs.",
    whatItDoes:
      "Says which party is responsible for each part of the food safety picture around your product: the plant's food safety plan, sanitation, environmental monitoring, allergen control, foreign material control, supplier approval for ingredients they buy, hold and release, traceability exercises and notification when something goes wrong.",
    whyItMatters:
      "Food safety programs live at the plant, but the brand on the pack is what a consumer, a retailer and a journalist see. That asymmetry is the whole reason to write this down. The practical questions are who runs each program, who pays for it, who has to tell you when a result comes back wrong, and how fast — and the last of those is the one most often left out, which is how founders learn about a problem from a customer rather than from their manufacturer.",
    majorTerms: [
      "Responsibility for the plant's food safety plan and its maintenance",
      "Facility registration status and notice of any change to it",
      "Sanitation program, schedule and verification",
      "Environmental monitoring program and how results are shared",
      "Allergen control program and validated changeover procedures",
      "Foreign material control: screening, detection and rejection",
      "Approved supplier program for ingredients the plant purchases",
      "Incoming material inspection and acceptance",
      "Hold, release and disposition procedures",
      "Traceability capability and mock trace exercises",
      "Notification obligations for adverse findings and inspections",
      "Training, hygiene practices and visitor controls",
      "Corrective action and follow-up verification",
    ],
    clausesToWatch: [
      {
        clause: "Notification of adverse findings",
        why: "Without an obligation to tell you promptly about a positive environmental result or a failed test on your line, you cannot make decisions about product you are already selling.",
      },
      {
        clause: "Allergen changeover and scheduling",
        why: "If nothing commits the plant to a validated changeover or to scheduling your product away from allergen-containing runs, your allergen statement rests on an assumption.",
      },
      {
        clause: "Notification of regulatory inspections and their outcomes",
        why: "Learning that an inspection took place at your manufacturer months later removes any chance to assess what it means for your product.",
      },
      {
        clause: "Approved supplier program for ingredients the plant buys",
        why: "If the plant can source from any supplier it likes, the food safety controls behind your ingredients are invisible to you.",
      },
      {
        clause: "Traceability and mock recall obligations",
        why: "A trace exercise that has never been practised takes days instead of hours, and the width of a recall is decided by how fast you can narrow it.",
      },
      {
        clause: "Cost responsibility for testing and monitoring",
        why: "Programs that nobody has agreed to fund quietly become programs that are not run.",
      },
    ],
    cpgIssues: [
      "Allergen control is the highest-consequence item in most food plants: which allergens run in the facility, on which lines, in what sequence, and whether changeover is validated rather than assumed.",
      "Environmental monitoring results are the early warning system for a problem on your line, and they are worth nothing to you if you never see them.",
      "Foreign material detection capability — metal detection, screening, filtration — varies enormously between plants and should be described, not assumed.",
      "Water activity, pH, thermal process or other controls that make your specific product safe are product-specific; the agreement should tie them to your specification rather than to the plant's general program.",
      "FDA facility registration and third-party certification are the plant's to hold and maintain, and a lapse in either can stop your production or remove a claim from your pack, so the agreement should allocate who keeps them current and require prompt notice of any change in status.",
      "Traceability needs to reach one step back and one step forward with lot-level detail, or every trace exercise becomes a full recall.",
    ],
    founderMistakes: [
      "Never asking which allergens run in the facility, then discovering a shared line after the label had already been printed.",
      "Assuming a third-party certification meant every relevant program was in place and verified for their specific product.",
      "Having no notification clause, and hearing about a hold on their product from a distributor rather than the plant.",
      "Not requiring participation in mock trace exercises, then attempting the first one during a real event.",
      "Treating food safety as entirely the plant's problem, and having no documentation to show a retailer during vendor qualification.",
    ],
    negotiationPoints: [
      "Require prompt written notice of any adverse result, hold, or regulatory inspection touching your product or the lines it runs on.",
      "Ask for a written description of the allergen profile of the facility and the lines your product runs on.",
      "Secure a right to receive environmental monitoring trends relevant to your production areas.",
      "Require an approved supplier program with your right to review the list for ingredients in your product.",
      "Ask for an annual mock trace exercise involving your SKUs, with the results shared.",
      "Agree in writing who funds testing and monitoring so no program depends on nobody objecting.",
    ],
    redFlags: [
      "No obligation to notify you of adverse findings, holds or inspections.",
      "Refusal to describe the facility's allergen profile in writing.",
      "No traceability commitment at lot level.",
      "Food safety responsibility described only as 'the manufacturer will comply with applicable law' and nothing further.",
      "No right to visit, audit or receive audit results.",
      "Certification status with no obligation to tell you if it lapses.",
    ],
    beforeYouSign: [
      "A written statement of the allergens present in the facility and on the lines running your product.",
      "The plant's current certifications and the most recent third-party audit result with corrective actions.",
      "A summary of the food safety plan as it applies to your product category.",
      "Your own product's critical control points, so the agreement can reference something specific rather than generic.",
    ],
    related: [
      "quality-agreement",
      "quality-assurance-agreement",
      "recall-responsibility-agreement",
      "supplier-quality-agreement",
      "co-packer-agreement",
      "product-specification-sheet",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Allocating food safety responsibility affects your exposure to regulators, retailers and consumers at once, and the consequences of getting the allocation wrong are not limited to a commercial loss.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "specification-agreement",
    name: "Specification Agreement",
    category: "manufacturing",
    subcategory: "Specifications",
    aliases: ["Spec Control Agreement", "Specification Governance Agreement", "Technical Specification Agreement"],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Once you have more than one specification, more than one SKU or more than one plant, and the question 'which version is current' stops having an obvious answer.",
    whatItDoes:
      "Governs how specifications are created, numbered, approved, distributed and changed: which version controls at any moment, who may approve a revision, and what happens when the specification and a physical sample disagree.",
    whyItMatters:
      "The specification sheet is the data. This agreement is the process around it, and process is what fails at scale. Two plants running two versions of the same specification produce two different products under one label — and the version confusion is usually discovered by a customer who noticed the difference. It is also what stops a plant treating an informal email as an approved change.",
    majorTerms: [
      "Specification numbering and version control",
      "Who may propose and who must approve a revision",
      "Effective date of a revision and how in-flight production is treated",
      "Distribution of current versions to all sites and suppliers",
      "Retention of superseded versions",
      "Relationship between the written specification and physical reference standards",
      "Tolerances and what counts as within specification",
      "Handling of conflicts between the specification and any other document",
      "Applicability across multiple SKUs, plants and co-manufacturers",
      "Records and audit of specification changes",
    ],
    clausesToWatch: [
      {
        clause: "Who can approve a change",
        why: "If a plant supervisor's email can move a specification, the controlling document becomes whatever was said last rather than what was agreed.",
      },
      {
        clause: "Effective date and in-flight production",
        why: "Without a stated effective date, a revision can be applied mid-run and produce a lot that matches neither version.",
      },
      {
        clause: "Conflict between the specification and the supply agreement",
        why: "Two documents describing acceptance differently means a rejected run turns into a paperwork argument.",
      },
      {
        clause: "Physical reference standards",
        why: "Colour, texture and flavour are hard to write down, and without an agreed physical or sensory reference these disputes have no resolution path.",
      },
      {
        clause: "Distribution to multiple sites",
        why: "A revision approved with one plant and never sent to the second means the same product is made two different ways.",
      },
    ],
    cpgIssues: [
      "A specification change forced by an ingredient substitution or a label revision has downstream effects on artwork, shelf-life data and retailer listing information, so the change process needs to reach beyond the plant.",
      "Sensory attributes need a reference standard and a defined panel or comparison method, because 'tastes right' is the most common and least resolvable quality dispute in food and beverage.",
      "Version control across co-manufacturers matters most for brands running the same SKU at two plants, where drift between sites is invisible until a customer compares two cans.",
      "Superseded specifications should be retained for at least the shelf life of product made under them, because a complaint about an older lot must be judged against the version in force at the time.",
      "Packaging specifications and product specifications change on different clocks; the governance process should link them so a can change does not orphan a fill specification.",
    ],
    founderMistakes: [
      "Approving a formula tweak by email, then having no record of which lots were made under which version when a complaint arrived.",
      "Running one SKU at two co-packers with different specification versions, and shipping visibly different product to the same retailer.",
      "Not retaining superseded specifications, so an old lot could not be assessed against the standard that applied to it.",
      "Letting the plant hold the master specification, then losing access to the current version during a dispute.",
      "Never agreeing a sensory reference, so every off-taste complaint became a matter of opinion.",
    ],
    negotiationPoints: [
      "Require written, signed approval by named roles for every specification revision.",
      "Set a stated effective date convention and rules for in-flight production.",
      "Keep the master version under your control, with distribution obligations to every site.",
      "Agree a physical or sensory reference standard alongside the written specification.",
      "Set a retention period for superseded versions tied to shelf life.",
      "Add a precedence clause so the specification controls acceptance over any conflicting document.",
    ],
    redFlags: [
      "Changes permitted by informal communication.",
      "No version numbering or effective dating.",
      "The plant holding the only master copy.",
      "No route for resolving sensory or appearance disputes.",
      "No obligation to distribute revisions to other sites or suppliers.",
    ],
    beforeYouSign: [
      "A complete inventory of the specifications you currently have and which version each plant is using.",
      "A decision on who inside your business owns specification approval.",
      "An agreed sensory or physical reference standard for each SKU.",
    ],
    related: [
      "product-specification-sheet",
      "packaging-specification-agreement",
      "manufacturing-change-control-agreement",
      "quality-agreement",
      "co-packer-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "product-specification-sheet",
    name: "Product Specification Sheet",
    category: "manufacturing",
    subcategory: "Specifications",
    aliases: ["Spec Sheet", "Finished Product Specification", "Product Spec"],
    stages: ["development", "pre-launch", "manufacturing", "launch"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer (signed by both)",
    whenYouNeedIt:
      "Before the first commercial run, and attached to every manufacturing, quality and supply agreement you sign. Retailers and distributors will also ask for it during onboarding.",
    whatItDoes:
      "Defines the product in measurable terms: ingredients and declaration, process parameters, finished attributes, microbiological limits, net contents, shelf life, packaging components, allergen statement and the tests that prove a lot conforms.",
    whyItMatters:
      "This is the document that defines the phrase 'correct product'. Every argument about a bad run, every rejection, every credit and every recall decision is measured against it. Founders who never signed one find that a run which is visibly wrong is still contractually fine, because nothing said what right was. It is also the cheapest document in this entire category to produce and the one most often missing.",
    majorTerms: [
      "Product name, SKU, version number and effective date",
      "Ingredient statement in declared order and the batch formula reference",
      "Allergen statement and cross-contact status",
      "Process parameters: mix order, temperatures, hold times, fill conditions",
      "Finished product attributes and target ranges — pH, Brix, viscosity, colour, carbonation, water activity as applicable",
      "Microbiological limits and test methods",
      "Net contents, fill target and tolerance",
      "Shelf life, storage conditions and date code format",
      "Packaging components by supplier part number",
      "Label version and artwork reference",
      "Certificate of Analysis parameters required per lot",
      "Sensory reference standard and evaluation method",
      "Acceptance criteria and rejection thresholds",
      "Signatures and approval date from both parties",
    ],
    clausesToWatch: [
      {
        clause: "Tolerances and ranges",
        why: "An attribute with a target but no tolerance cannot be enforced, and a tolerance set too wide accepts product your customers will notice is different.",
      },
      {
        clause: "Which document controls on a conflict",
        why: "If the manufacturing agreement and the specification describe acceptance differently, a rejection becomes an argument about paperwork rather than product.",
      },
      {
        clause: "Packaging components by part number",
        why: "Describing packaging generically lets a substitute component appear that does not run on the line or does not match the artwork.",
      },
      {
        clause: "Certificate of Analysis parameters",
        why: "If the specification does not list what a COA must report, you receive a document that confirms nothing you can act on.",
      },
      {
        clause: "Sensory standard",
        why: "Without an agreed reference and method, flavour and appearance complaints have no route to resolution.",
      },
      {
        clause: "Version and effective date",
        why: "An unversioned specification makes it impossible to say which standard applied to a lot produced six months ago.",
      },
    ],
    cpgIssues: [
      "Net contents and fill tolerance are a specification matter with regulatory consequences, so the target and the acceptable range belong in writing rather than in the plant's habit.",
      "Allergen status must cover both the formula and cross-contact in the facility, since the label statement depends on both.",
      "Shelf life should be tied to the stability data that produced it and to defined storage conditions, not asserted as a round number.",
      "Date code format and placement matter for a trace exercise and for retailer receiving, and are routinely left out of the specification.",
      "Packaging part numbers link the product specification to the packaging specification, which is how a can or label change gets caught before it reaches the line.",
      "Micro limits and methods should be named specifically; two laboratories using different methods can produce different answers about the same lot.",
    ],
    founderMistakes: [
      "Producing for a year with no signed specification, then having no standard to point to when a run came out wrong.",
      "Writing targets with no tolerances, so every value was technically acceptable.",
      "Leaving packaging described generically, and receiving a run in a substituted can that jammed the line at the next production.",
      "Putting a shelf life on the pack that no stability data supported, and having no answer when a retailer asked for the study.",
      "Never versioning the sheet, so nobody could say which specification a complaint lot was made under.",
    ],
    negotiationPoints: [
      "Get the specification signed and dated by both parties before the first commercial run.",
      "Add a precedence clause making the specification controlling for acceptance and rejection.",
      "Include tolerances for every measurable attribute, set against your own quality expectations rather than the plant's default.",
      "List required COA parameters and methods directly on the sheet.",
      "Reference packaging components by supplier part number and artwork version.",
      "Agree the sensory reference standard and how disputes about it are resolved.",
    ],
    redFlags: [
      "A specification the manufacturer will not sign.",
      "Attributes listed with targets but no acceptable ranges.",
      "Shelf life stated with no supporting stability data.",
      "No allergen or cross-contact statement.",
      "No version number or effective date.",
    ],
    beforeYouSign: [
      "Stability and shelf-life data supporting the dating you intend to print.",
      "Confirmed packaging component part numbers from each supplier.",
      "Your finalised label copy, so the ingredient and allergen statements match the specification.",
    ],
    related: [
      "specification-agreement",
      "packaging-specification-agreement",
      "quality-agreement",
      "co-packer-agreement",
      "shelf-life-testing-agreement",
      "manufacturing-change-control-agreement",
    ],
    attorneyReview: "depends",
    templateId: "product-specification-sheet",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "packaging-specification-agreement",
    name: "Packaging Specification Agreement",
    category: "manufacturing",
    subcategory: "Specifications",
    aliases: ["Packaging Spec", "Component Specification Agreement", "Artwork and Component Specification"],
    stages: ["pre-launch", "manufacturing", "launch"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer or Packaging Supplier",
    whenYouNeedIt:
      "Before ordering any printed or custom packaging component, and before the first run on a line that has never handled your can, bottle, closure or label.",
    whatItDoes:
      "Defines each packaging component in measurable terms — dimensions, materials, coatings, print colours and tolerances, artwork version, barcode requirements, date code location, case count and pallet configuration — and states who approves changes to any of it.",
    whyItMatters:
      "Packaging is where a specification error becomes an immediate physical problem. A can with the wrong neck does not run on the filler. A label with the wrong wind direction jams the applicator. A carton a few millimetres out of tolerance breaks the pallet pattern and the retailer's receiving. Every one of those is discovered on production day, with the line booked and materials on the floor, and every one of them is preventable with a document nobody wanted to write.",
    majorTerms: [
      "Component list with supplier part numbers",
      "Dimensions and tolerances for each component",
      "Material, gauge, board grade or substrate",
      "Coatings, liners, adhesives and closure compatibility",
      "Print colours, colour tolerance and proofing method",
      "Artwork version, dieline reference and approval record",
      "Barcode symbology, placement and print quality requirement",
      "Date code format, location and application method",
      "Case count, case dimensions and pallet pattern",
      "Line compatibility: speed, applicator, seamer or capper settings",
      "Inbound acceptance criteria and defect thresholds",
      "Change approval process and notice period",
    ],
    clausesToWatch: [
      {
        clause: "Line compatibility confirmation",
        why: "A component that meets its own specification can still be unrunnable on a specific line, and the cost of finding that out lands on production day.",
      },
      {
        clause: "Colour tolerance and proofing",
        why: "Without an agreed colour tolerance and an approved proof, a print run that looks wrong on shelf is still within a supplier's idea of acceptable.",
      },
      {
        clause: "Artwork version control",
        why: "Printing against a superseded artwork file produces a full minimum order of packaging you cannot use.",
      },
      {
        clause: "Inbound acceptance and defect thresholds",
        why: "Dented cans, ovality, scuffed print and mis-wound label rolls are normal at some rate, and without a stated threshold you absorb all of it.",
      },
      {
        clause: "Change approval and notice",
        why: "Suppliers change substrate, gauge or adhesive for their own reasons, and an unannounced change can stop a line or alter shelf life.",
      },
      {
        clause: "Barcode print quality requirement",
        why: "A barcode that scans in your office and fails at a retailer's receiving dock becomes a chargeback and a delivery refusal.",
      },
    ],
    cpgIssues: [
      "Cans and labels carry long lead times and large minimums, so a specification error is not a reprint — it is a stranded minimum order and a production date you will miss.",
      "Label substrate and adhesive have to survive the actual condition of the product: condensation on a chilled beverage, hot fill, ice water immersion or a freezer, none of which a generic adhesive handles equally.",
      "Roll direction, wind, core size and unwind orientation must match the applicator at the specific plant; the same label on the wrong wind is unusable.",
      "Can neck finish, end and tab specification must match both the filler and the seamer at the plant that will run it, and these vary between facilities.",
      "Date code placement interacts with artwork: a code printed over a design element is unreadable in a trace exercise and can fail retailer receiving.",
      "Pallet pattern and case dimensions are retailer-facing; a case that does not fit the pattern costs freight on every load and can be rejected at the dock.",
    ],
    founderMistakes: [
      "Ordering printed cans against a design approved on screen with no colour proof, then receiving a full minimum order in the wrong shade.",
      "Not confirming label wind direction with the co-packer, and having a truckload of labels that the applicator could not run.",
      "Approving a case size that broke the pallet pattern, paying more freight on every load for a year.",
      "Letting the printer change adhesive without notice, and having labels lift off chilled cans in a distributor's warehouse.",
      "Never specifying date code location, so codes were printed over artwork and could not be read during a trace exercise.",
    ],
    negotiationPoints: [
      "Require a physical proof or first-article approval before any full print run.",
      "Get written confirmation from the plant that each component runs on the specific line at the quoted speed.",
      "Set inbound defect thresholds with a credit or replacement remedy above them.",
      "Require notice and your approval before any change to substrate, gauge, coating or adhesive.",
      "Specify barcode print quality and require verification, not just visual inspection.",
      "Tie artwork version numbers into the specification so a superseded file cannot be printed against.",
    ],
    redFlags: [
      "No colour tolerance or proofing step before a full print run.",
      "No confirmation that components run on the intended line.",
      "Supplier free to change materials without notice.",
      "No inbound acceptance criteria or defect remedy.",
      "Artwork referenced by name only, with no version control.",
    ],
    beforeYouSign: [
      "Written confirmation from the co-packer that the component specification is runnable on their line.",
      "An approved physical proof or first article for every printed component.",
      "The pallet pattern and case dimensions your intended retailers and freight carriers expect.",
      "Final artwork with a version number, matched to the label copy on the product specification.",
    ],
    related: [
      "product-specification-sheet",
      "specification-agreement",
      "packaging-supply-agreement",
      "can-supply-agreement",
      "label-supply-agreement",
      "packaging-artwork-ownership",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "manufacturing-change-control-agreement",
    name: "Manufacturing Change Control Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["Change Notification Agreement", "Change Control Terms", "Notification of Change Agreement"],
    stages: ["manufacturing", "launch", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Once production is running and the plant has enough latitude to change something — an ingredient supplier, a process parameter, a packaging component, a line or a site — without asking you.",
    whatItDoes:
      "Requires advance notice and, for defined categories, your approval before the manufacturer changes anything that affects your product, and sets what happens next: qualification runs, revised specifications, updated documentation and the right to refuse.",
    whyItMatters:
      "Nothing about your product is as stable as it looks. Ingredient suppliers get swapped for cost or availability, processes get tuned, lines get reassigned, packaging components get substituted. Each of those can change an allergen statement, invalidate shelf-life data, break a certification claim on your label or alter how the product tastes — and none of them is visible to you unless someone is obliged to say so. The founders who get hurt here are the ones who assumed a change big enough to matter would obviously be mentioned.",
    majorTerms: [
      "Categories of change requiring notice versus prior approval",
      "Notice period before a change takes effect",
      "Ingredient and supplier changes, including sub-suppliers",
      "Formula and process parameter changes",
      "Packaging component and material changes",
      "Line, equipment and facility changes",
      "Sanitation chemical and allergen program changes",
      "Qualification run requirements before a change goes commercial",
      "Documentation updates: specification, COA, shelf life, label data",
      "Your right to refuse a change or to terminate if it is made anyway",
      "Emergency changes and after-the-fact notification",
    ],
    clausesToWatch: [
      {
        clause: "Definition of a notifiable change",
        why: "If notice is required only for changes the plant considers significant, the plant decides what you get told about.",
      },
      {
        clause: "Notice period",
        why: "Notice given the week a change takes effect is information, not a decision point — you need enough time to qualify or refuse.",
      },
      {
        clause: "Sub-supplier changes",
        why: "A change one level down the chain can alter origin, allergen status or certification while the plant's own supplier stays the same on paper.",
      },
      {
        clause: "Consequences of an unapproved change",
        why: "Without a stated remedy, a change made without notice costs you a re-qualification and costs them nothing.",
      },
      {
        clause: "Emergency change carve-out",
        why: "A broad emergency exception can absorb the whole clause if 'emergency' is left to the plant to define.",
      },
      {
        clause: "Who pays for qualification runs",
        why: "If you fund every qualification triggered by their cost-saving change, their savings are financed by you.",
      },
    ],
    cpgIssues: [
      "An ingredient supplier swap can change allergen status, organic or non-GMO eligibility and declared country of origin — all of which appear on a label you have already printed.",
      "Process changes can invalidate shelf-life and stability data, which means the date code on the pack is no longer supported by anything.",
      "A facility or line change moves your product into a different allergen environment and a different certification scope.",
      "Certification-dependent claims — organic, kosher, non-GMO, gluten-free — are only as good as the current status of every input and the plant, and change control is how you find out before a retailer does.",
      "Packaging substitutions affect line performance and can quietly change case dimensions or pallet pattern, which shows up as a freight cost and a receiving problem.",
      "Retailers hold you to the product data you submitted at listing; a silent change makes your own submitted data wrong.",
    ],
    founderMistakes: [
      "Discovering an ingredient supplier had changed only when a customer with an allergy asked a question the founder could not answer.",
      "Accepting a change control clause covering only formula changes, which left process, packaging and site changes entirely unregulated.",
      "Paying for a full qualification run after a plant switched a component to save cost on their side.",
      "Not updating the specification after an approved change, so the controlling document and the actual product diverged.",
      "Having no remedy for an unapproved change, so the only options were accept it or start a fight.",
    ],
    negotiationPoints: [
      "Define notifiable changes by category rather than by significance, so the judgement is not theirs alone.",
      "Set a notice period long enough to run a qualification batch and assess label impact.",
      "Extend the obligation to sub-supplier and raw material origin changes.",
      "Require the plant to fund qualification work for changes they initiate for their own benefit.",
      "Add a right to refuse a change, and a remedy if one is made without notice.",
      "Require the specification, COA parameters and shelf-life documentation to be updated as part of any approved change.",
    ],
    redFlags: [
      "Notice required only for changes the manufacturer deems material.",
      "No obligation covering packaging, line or facility changes.",
      "A broad emergency exception with no after-the-fact notification.",
      "No consequence at all for making a change without notice.",
      "Qualification costs assigned to you regardless of who initiated the change.",
    ],
    beforeYouSign: [
      "A written list of every input and process parameter that affects a claim on your label.",
      "The current specification version, so approved changes have something to be applied to.",
      "Your own internal process for who assesses a change notice and how fast.",
    ],
    related: [
      "quality-agreement",
      "specification-agreement",
      "product-specification-sheet",
      "supplier-quality-agreement",
      "co-packer-agreement",
      "food-safety-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
  {
    id: "recall-responsibility-agreement",
    name: "Recall Responsibility Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["Recall Agreement", "Product Withdrawal Agreement", "Recall Cost Allocation Agreement"],
    stages: ["pre-launch", "manufacturing", "launch", "distribution"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Brand + Manufacturer",
    whenYouNeedIt:
      "Before the first commercial production run. Also whenever you add a manufacturer, a co-manufacturer or a significant ingredient supplier.",
    whatItDoes:
      "Decides in advance who can call a recall, who runs it, who talks to retailers and the public, and who pays for each category of cost — before anyone has an incentive to argue about it.",
    whyItMatters:
      "A recall is the fastest way a small CPG brand becomes insolvent, and almost none of the cost is the product itself. It is freight both ways, retailer chargebacks and handling fees, disposal, notification, a third-party recall firm, lost sales, and the cost of getting back on a shelf you were removed from. When the agreement is silent, all of that lands on the brand by default while the parties argue about root cause. Deciding this while everyone is calm is one of the highest-value hours a founder can spend.",
    majorTerms: [
      "Who has authority to initiate a recall or withdrawal",
      "Decision and escalation timeline once an issue is identified",
      "Who leads execution and who communicates with retailers, distributors and consumers",
      "Cost categories covered: product, freight, chargebacks, disposal, notification, third-party firm",
      "Root cause determination and how costs follow it",
      "Indemnification tied to fault and to the specification",
      "Recall and contamination insurance: who carries it and at what limits",
      "Product liability insurance and additional insured status",
      "Traceability obligations and required response times",
      "Mock recall exercises and their frequency",
      "Record retention supporting a trace",
      "Limitation of liability and its interaction with recall costs",
    ],
    clausesToWatch: [
      {
        clause: "Who may initiate a recall",
        why: "If the manufacturer must agree before product is pulled, you can be blocked from acting on your own brand while the risk keeps shipping.",
      },
      {
        clause: "Definition of recoverable costs",
        why: "A clause covering only 'the value of the affected product' leaves out freight, chargebacks, disposal and notification, which are most of the real cost.",
      },
      {
        clause: "Limitation of liability interacting with recall",
        why: "A liability cap set at the value of a production run can make the indemnity meaningless in exactly the event it was written for.",
      },
      {
        clause: "Root cause and burden of proof",
        why: "If the party seeking recovery has to prove cause with evidence held by the other party, the clause is unusable in practice.",
      },
      {
        clause: "Recall insurance versus product liability insurance",
        why: "These are different policies covering different costs, and assuming one covers the other is how brands discover they were uninsured for the expensive part.",
      },
      {
        clause: "Traceability response time",
        why: "The width of a recall is determined by how precisely and how quickly you can identify affected lots, and slow tracing turns a one-lot issue into everything on the market.",
      },
      {
        clause: "Public communication control",
        why: "Whoever speaks first defines the story about your brand, and an agreement silent on communications leaves that to whoever is fastest.",
      },
    ],
    cpgIssues: [
      "Retailer chargebacks and handling fees during a recall frequently exceed the wholesale value of the product being recalled, and standard clauses rarely mention them.",
      "Distributors hold inventory across many accounts; a recall reaches into their warehouses, and their agreements have their own cost-recovery terms that need to line up with this one.",
      "Recall insurance is a specific product distinct from general product liability cover, and small brands routinely carry the second while assuming it does the job of the first.",
      "Lot-level traceability from ingredient receipt to finished pallet is the single biggest determinant of recall size, and it depends on coding practices agreed in the quality documents.",
      "Cold chain and shelf-life products create additional disposal and freight cost, because recovered product cannot simply be returned to stock.",
      "Mock recall exercises are cheap and reveal where the trace breaks; the obligation to run one belongs in the agreement rather than in good intentions.",
    ],
    founderMistakes: [
      "Signing a co-packing agreement with no recall clause because the topic felt remote at pre-launch.",
      "Assuming the co-packer's product liability policy would fund a recall, then finding recall costs were a separate cover nobody had bought.",
      "Accepting a liability cap equal to the value of the affected run, which made the indemnity almost worthless.",
      "Having no lot traceability between ingredient receipt and finished pallets, and recalling every lot in the market because narrowing it was impossible.",
      "Not securing the right to initiate a withdrawal without the manufacturer's agreement, and losing days while the decision was debated.",
    ],
    negotiationPoints: [
      "Keep unilateral authority to initiate a recall or withdrawal of your own product.",
      "List recoverable cost categories explicitly rather than relying on a general indemnity.",
      "Carve recall costs and third-party bodily injury claims out of the general liability cap.",
      "Require both recall and product liability insurance at stated limits, with certificates and additional insured status.",
      "Set a traceability response time measured in hours, with an annual mock exercise to prove it.",
      "Agree who communicates with retailers, distributors and media, and require coordination before any public statement.",
    ],
    redFlags: [
      "No recall clause at all.",
      "Recovery limited to the invoice value of affected product.",
      "A general liability cap that also caps recall and injury claims.",
      "The manufacturer holding veto power over your decision to recall.",
      "No insurance requirement, or no evidence of recall-specific cover.",
      "No traceability commitment or response time.",
    ],
    beforeYouSign: [
      "Certificates of insurance for both product liability and recall cover, with limits and named insureds.",
      "A written trace exercise result showing how quickly affected lots can be identified.",
      "Your own recall plan naming who does what in the first twenty-four hours.",
      "Alignment with your distributor and retailer agreements on how recall costs flow through the chain.",
    ],
    related: [
      "co-packer-agreement",
      "quality-agreement",
      "food-safety-agreement",
      "product-specification-sheet",
      "supplier-quality-agreement",
      "ingredient-supply-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Recall allocation combines insurance, indemnity and liability caps across several agreements at once, and an error here is the difference between a bad quarter and the end of the business.",
    templateId: "recall-responsibility-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },
  {
    id: "supplier-quality-agreement",
    name: "Supplier Quality Agreement",
    category: "manufacturing",
    subcategory: "Quality and food safety",
    aliases: ["Ingredient Quality Agreement", "Vendor Quality Agreement", "Supplier Quality Requirements"],
    stages: ["pre-launch", "manufacturing", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand or Co-Packer + Ingredient or Packaging Supplier",
    whenYouNeedIt:
      "When you buy ingredients or packaging directly rather than through the co-packer, or when a retailer's vendor qualification asks how your inputs are controlled.",
    whatItDoes:
      "Sets the quality obligations behind a supply relationship: incoming specifications, certificates of analysis, documentation for certifications and allergens, change notification, audit rights, handling of non-conforming material and cooperation in a trace or recall.",
    whyItMatters:
      "Most quality problems in a finished product started as a problem in an input. The commercial supply agreement covers price and delivery; this covers whether what arrives is actually what you specified and whether you can prove it. Without it, a supplier can change origin, grade or sub-supplier without telling anyone, and the first evidence is a finished product that does not behave the way it used to.",
    majorTerms: [
      "Incoming material specification and version control",
      "Certificate of Analysis per lot: parameters, methods and delivery timing",
      "Allergen status of the material and of the supplier's facility",
      "Certification documentation: organic, kosher, non-GMO, gluten-free as applicable",
      "Country of origin and sub-supplier disclosure",
      "Change notification for source, site, process or sub-supplier",
      "Audit rights and access to third-party audit results",
      "Non-conforming material: rejection, return, replacement and cost",
      "Lot coding, traceability and record retention",
      "Recall and trace cooperation obligations and response times",
      "Continuing assurances about the material's suitability for food use",
      "Delivery condition requirements including temperature and packaging integrity",
    ],
    clausesToWatch: [
      {
        clause: "COA contents and methods",
        why: "A certificate that reports only 'meets specification' cannot be used to investigate a problem or satisfy a retailer's question.",
      },
      {
        clause: "Change notification covering sub-suppliers",
        why: "A supplier can keep its own name on the paperwork while the material behind it comes from somewhere entirely different.",
      },
      {
        clause: "Non-conforming material remedy",
        why: "Replacement of the material alone ignores the far larger cost of the production run it ruined.",
      },
      {
        clause: "Allergen and facility status disclosure",
        why: "Cross-contact at the supplier's plant affects your label statement, and without disclosure you are declaring something you cannot verify.",
      },
      {
        clause: "Trace response time",
        why: "If a supplier is not obliged to respond quickly with lot data, your recall gets wider while you wait.",
      },
      {
        clause: "Audit rights",
        why: "No right to audit or to see audit results means you are relying on a certificate rather than on any verification.",
      },
    ],
    cpgIssues: [
      "Agricultural and botanical inputs vary by crop year and origin, so the specification needs ranges that reflect natural variation without accepting material that changes the finished product.",
      "Organic, kosher and non-GMO claims on your pack depend on current certificates for every relevant input, so certificate expiry dates need to be tracked as a supply obligation.",
      "Allergen status of an ingredient depends on the supplier's facility as well as the material itself, and a supplier facility change can alter your label statement.",
      "Flavour and colour systems from a flavour house are often supplied under a proprietary designation, which makes the specification and COA the only visibility you have into what is in them.",
      "Delivery condition matters for temperature-sensitive and moisture-sensitive inputs; a specification that ignores transit condition accepts material that was compromised before it arrived.",
      "Where the co-packer buys the ingredient, this agreement usually sits between them and the supplier — so you need a right to see it, or equivalent protection in your own manufacturing agreement.",
    ],
    founderMistakes: [
      "Buying an ingredient on price with no quality agreement, then receiving a lot that met a generic specification but produced a visibly different finished product.",
      "Accepting COAs that stated conformance without any values, and having nothing to investigate with when a run failed.",
      "Not requiring certificate expiry tracking, and discovering an organic certificate had lapsed after product carrying the claim had shipped.",
      "Having no sub-supplier change notification, and finding the origin of a key input had moved without any notice.",
      "Assuming the co-packer had a quality agreement with the ingredient supplier, and finding there was none.",
    ],
    negotiationPoints: [
      "Require a COA per lot with named parameters, methods and values, delivered with or before the shipment.",
      "Extend change notification to sub-suppliers, sites, processes and country of origin.",
      "Ask for a remedy that covers the downstream cost of a non-conforming lot, not just replacement of the material.",
      "Require written disclosure of the allergen profile of the supplier's facility.",
      "Set a trace response time in hours and test it with a mock exercise.",
      "Require current certification documents on file and prompt notice of any lapse or change.",
    ],
    redFlags: [
      "COAs that state conformance without reporting values.",
      "No obligation to notify you of a change of source, site or sub-supplier.",
      "Remedy limited to replacing the defective material.",
      "Refusal to disclose facility allergen status.",
      "No traceability or recall cooperation obligation.",
      "Certifications supplied once and never updated.",
    ],
    beforeYouSign: [
      "A written incoming specification for each material, with ranges that reflect real variation.",
      "Current certificates for every claim your label depends on, with expiry dates.",
      "A sample COA from the supplier so you can see what they actually report.",
      "Confirmation of whether the co-packer or you holds the buying relationship, so the agreement sits between the right parties.",
    ],
    related: [
      "ingredient-supply-agreement",
      "quality-agreement",
      "food-safety-agreement",
      "manufacturing-change-control-agreement",
      "recall-responsibility-agreement",
      "raw-material-supply-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default MANUFACTURING_DOCS;
